import React, { useState } from 'react';
import Modal from '../Modal';
import { contractsApi, errorOf, DOC_LABEL } from '../../lib/contracts';
import { PERIOD_LABEL } from '../../lib/licences';
import NoticeFields from '../licences/NoticeFields';
import AttachFiles, { uploadAll } from '../licences/AttachFiles';
import '../licences/Licences.css';
import './Contracts.css';

/**
 * Add a contract, or change one: who it is with and for which company, the
 * title, scope and reference, its dates and whether it renews by itself,
 * the notice period, the caps, who looks after it and the notes. Adding one
 * also takes its first hourly rate (from the start date) and whether it is
 * a draft or active; later rates go on the rate card on its page.
 *
 * The server checks everything again and says what is wrong in a sentence
 * (lib/contracts.js clean), which is shown above the buttons. `options` is
 * GET /contracts/options. `contractorId` fixes the contractor (adding from a
 * contractor's profile).
 *
 * Wave 7 (130): the notice period in days or months and the reminder before
 * its deadline (components/licences/NoticeFields - before 130, days only and
 * no reminder), and on adding, the files to attach, uploaded once the
 * contract exists; `onSaved(saved, failed)` carries any that were not.
 */
const blank = {
  contractor_id: '', company_id: '', title: '', scope: '', reference: '', start_date: '', end_date: '', renewal_date: '',
  auto_renew: false, billing_period: '', notice_amount: '', notice_unit: 'days', notice_alert_days: '', cap_hours: '', cap_amount: '', currency: 'EUR', owner_id: '', notes: ''
};
const asText = (v) => (v === null || v === undefined ? '' : String(v));
const fromContract = (k) => Object.fromEntries(Object.keys(blank).map((f) => [f, f === 'auto_renew' ? Boolean(k[f]) : asText(k[f]) || blank[f]]));

export default function ContractForm({ contract = null, contractorId = null, options, onClose, onSaved }) {
  const [form, setForm] = useState(() => (contract ? fromContract(contract) : { ...blank, contractor_id: contractorId || '' }));
  const [rate, setRate] = useState('');
  const [makeActive, setMakeActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [files, setFiles] = useState([]);
  const noticeReady = Boolean(options?.notice);
  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const contractors = options?.contractors || [];
  const people = options?.people || [];
  const companies = (options?.companies || []).filter((c) => !c.archived_at || c.id === form.company_id);
  const chosen = contractors.find((c) => c.id === form.contractor_id);

  const save = async (e) => {
    e.preventDefault();
    if (!contract && !form.contractor_id) { setProblem('Choose the contractor the contract is with.'); return; }
    if (!form.title.trim()) { setProblem('Give the contract a title.'); return; }
    if (!contract && makeActive && !form.start_date) { setProblem('An active contract needs its start date.'); return; }
    setBusy(true);
    setProblem('');
    const empty = (v) => (v === '' ? null : v);
    const body = {
      ...form,
      company_id: empty(form.company_id), owner_id: empty(form.owner_id), billing_period: empty(form.billing_period),
      start_date: empty(form.start_date), end_date: empty(form.end_date), renewal_date: empty(form.renewal_date),
      notice_amount: form.notice_amount === '' ? null : Number(form.notice_amount),
      notice_unit: noticeReady ? form.notice_unit : 'days',
      notice_alert_days: !noticeReady || form.notice_alert_days === '' || form.notice_amount === '' ? null : Number(form.notice_alert_days),
      cap_hours: empty(form.cap_hours), cap_amount: empty(form.cap_amount)
    };
    if (contract) delete body.contractor_id;
    else {
      body.status = makeActive ? 'active' : 'draft';
      if (rate !== '') body.hourly_rate = rate;
    }
    try {
      const saved = contract ? await contractsApi.update(contract.id, body) : await contractsApi.create(body);
      const failed = !contract && files.length
        ? await uploadAll(files, (f) => contractsApi.upload(saved.contract.id, f), errorOf) : [];
      onSaved(saved, failed);
    } catch (err) {
      setProblem(errorOf(err, 'The contract could not be saved.'));
      setBusy(false);
    }
  };

  const footer = (
    <>
      <button type="button" className="btn btn-plain" onClick={onClose} disabled={busy}>Cancel</button>
      <button type="submit" form="con-form" className="btn btn-primary" disabled={busy}>
        {busy ? 'Saving…' : contract ? 'Save changes' : 'Add the contract'}
      </button>
    </>
  );

  return (
    <Modal title={contract ? `Change ${contract.title}` : 'Add a contract'}
      subtitle={contract ? null : 'An agreement with a contractor: its dates, notice, rates and caps.'}
      onClose={onClose} footer={footer} wide>
      <form id="con-form" className="lic-form" onSubmit={save} noValidate data-testid="contract-form">
        {!contract && (
          <div className="lic-field">
            <label htmlFor="con-contractor">Contractor</label>
            <select id="con-contractor" value={form.contractor_id} onChange={set('contractor_id')} disabled={Boolean(contractorId)}>
              <option value="">Choose…</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.company ? `${c.name} (${c.company})` : c.name}</option>)}
            </select>
            {chosen && chosen.hourly_rate !== null && (
              <p className="lic-hint">{`Their own rate today: €${Number(chosen.hourly_rate).toFixed(2)} an hour.`}</p>
            )}
          </div>
        )}
        <div className="lic-field">
          <label htmlFor="con-company">For company</label>
          <select id="con-company" value={form.company_id} onChange={set('company_id')}>
            <option value="">No particular company</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <p className="lic-hint">A company’s contract prices that company’s tickets (their device’s company).</p>
        </div>
        <div className="lic-field">
          <label htmlFor="con-title">Title</label>
          <input id="con-title" value={form.title} onChange={set('title')} maxLength={160} placeholder="IT support 2027" autoComplete="off" />
        </div>
        <div className="lic-field">
          <label htmlFor="con-ref">Reference or PO</label>
          <input id="con-ref" value={form.reference} onChange={set('reference')} maxLength={120} autoComplete="off" />
        </div>
        <div className="lic-field lic-field-wide">
          <label htmlFor="con-scope">Scope</label>
          <textarea id="con-scope" rows={2} value={form.scope} onChange={set('scope')} maxLength={4000}
            placeholder="What the contractor does under it" />
        </div>
        <div className="lic-field">
          <label htmlFor="con-start">Starts</label>
          <input id="con-start" type="date" value={form.start_date} onChange={set('start_date')} />
        </div>
        <div className="lic-field">
          <label htmlFor="con-end">Ends</label>
          <input id="con-end" type="date" value={form.end_date} onChange={set('end_date')} />
          <p className="lic-hint">Leave empty if it runs until ended. It is marked ended after this day.</p>
        </div>
        <div className="lic-field">
          <label htmlFor="con-renewal">Renews</label>
          <input id="con-renewal" type="date" value={form.renewal_date} onChange={set('renewal_date')} />
        </div>
        <div className="lic-field">
          <label htmlFor="con-period">Renewal period</label>
          <select id="con-period" value={form.billing_period} onChange={set('billing_period')}>
            <option value="">Not set</option>
            {Object.entries(PERIOD_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="lic-field">
          <label className="lic-check">
            <input type="checkbox" checked={form.auto_renew} onChange={set('auto_renew')} />
            <span>Renews by itself
              <span className="lic-hint" style={{ display: 'block' }}>When the date passes, the next renewal is set one period on.</span>
            </span>
          </label>
        </div>
        <NoticeFields form={form} set={set} ready={noticeReady} prefix="con" daysOnly />
        <div className="lic-field">
          <label htmlFor="con-cap-hours">Hours cap</label>
          <input id="con-cap-hours" type="number" inputMode="decimal" min="0" step="0.5" value={form.cap_hours} onChange={set('cap_hours')}
            placeholder="No cap" />
        </div>
        <div className="lic-field">
          <label htmlFor="con-cap-amount">Money cap</label>
          <div className="lic-cost">
            <input id="con-cap-amount" type="number" inputMode="decimal" min="0" step="0.01" value={form.cap_amount}
              onChange={set('cap_amount')} placeholder="No cap" />
            <input aria-label="Currency" value={form.currency} onChange={(e) => set('currency')(e.target.value.toUpperCase().slice(0, 3))} maxLength={3} />
          </div>
          <p className="lic-hint">Counted against approved hours inside the contract’s dates; reminders at 80% and 100%.</p>
        </div>
        {!contract && (
          <div className="lic-field">
            <label htmlFor="con-rate">Hourly rate</label>
            <input id="con-rate" type="number" inputMode="decimal" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)}
              placeholder={chosen?.hourly_rate != null ? String(chosen.hourly_rate) : '0.00'} />
            <p className="lic-hint">From the start date. Category rates and later changes go on the rate card.</p>
          </div>
        )}
        <div className="lic-field">
          <label htmlFor="con-owner">Looked after by</label>
          <select id="con-owner" value={form.owner_id} onChange={set('owner_id')}>
            <option value="">Nobody in particular</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <p className="lic-hint">A ticket raised by an automation rule is raised for them.</p>
        </div>
        {!contract && (
          <div className="lic-field">
            <label className="lic-check">
              <input type="checkbox" checked={makeActive} onChange={(e) => setMakeActive(e.target.checked)} />
              <span>Active now
                <span className="lic-hint" style={{ display: 'block' }}>Its rates are used from the start date. Untick to keep it a draft.</span>
              </span>
            </label>
          </div>
        )}
        <div className="lic-field lic-field-wide">
          <label htmlFor="con-notes">Notes</label>
          <textarea id="con-notes" rows={3} value={form.notes} onChange={set('notes')} maxLength={4000} />
          <p className="lic-hint">For NCMB only - the contractor never sees them.</p>
        </div>
        {!contract && (
          <AttachFiles files={files} onChange={setFiles} docTypes={DOC_LABEL} defaultType="contract" onProblem={setProblem} prefix="con" />
        )}
        {problem && <p className="as-error lic-form-error" role="alert">{problem}</p>}
      </form>
    </Modal>
  );
}
